import api from './api';

const spaceService = {
  // Spaces
  getSpaces: (params) => api.get('/spaces', { params }),
  getSpace: (id) => api.get(`/spaces/${id}`),
  createSpace: (data) => api.post('/spaces', data),
  updateSpace: (id, data) => api.put(`/spaces/${id}`, data),

  // Zones — backend exposes `GET /zones?warehouse_id=` rather than a
  // nested `/warehouses/:id/zones` path. Keep both helpers so existing
  // callers don't break.
  listZones: (params = {}) => api.get('/zones', { params }),
  getZones: (warehouseId, params = {}) =>
    api.get('/zones', { params: { ...params, warehouse_id: warehouseId } }),
  getZone: (id) => api.get(`/zones/${id}`),
  // Accept either ``createZone(data)`` with ``warehouse_id`` already in
  // the body, OR the legacy ``(warehouseId, data)`` form redux thunks
  // still use. Folding the FK into a single dict guarantees the backend
  // never sees a bare string and 422s the whole request.
  createZone: (warehouseIdOrData, data) => {
    const isLegacyShape = typeof warehouseIdOrData === 'string' && data;
    const body = isLegacyShape
      ? { warehouse_id: warehouseIdOrData, ...data }
      : (warehouseIdOrData || {});
    return api.post('/zones', body);
  },
  updateZone: (id, data) => api.put(`/zones/${id}`, data),
  deleteZone: (id) => api.delete(`/zones/${id}`),

  // Allocations
  getAllocations: (params) => api.get('/allocations', { params }),
  getAllocation: (id) => api.get(`/allocations/${id}`),
  createAllocation: (data) => api.post('/allocations', data),
  // One-shot Space+Allocation creation for walk-in tenants. Payload
  // shape matches the QuickAllocateRequest backend schema: the host
  // picks a zone and an area; the backend creates the underlying
  // space row and allocates it in the same transaction.
  quickAllocate: (data) => api.post('/allocations/quick', data),
  updateAllocation: (id, data) => api.put(`/allocations/${id}`, data),
  releaseAllocation: (id) => api.post(`/allocations/${id}/release`),
  // Server-side rental math → billing-service handoff. Returns the
  // created invoice verbatim from billing-service so the UI can show
  // the new invoice_number + total_amount inline.
  generateAllocationInvoice: (id, data) =>
    api.post(`/allocations/${id}/generate-invoice`, data),

  // Utilisation
  getUtilisationOverview: (params) => api.get('/spaces/utilisation', { params }),
  getUtilisationHistory: (warehouseId, params) =>
    api.get(`/warehouses/${warehouseId}/utilisation/history`, { params }),

  // Racks
  listRacks: (params = {}) => api.get('/racks', { params }),
  getRack: (id) => api.get(`/racks/${id}`),
  createRack: (body) => api.post('/racks', body),
  updateRack: (id, body) => api.put(`/racks/${id}`, body),
  deleteRack: (id) => api.delete(`/racks/${id}`),
  generateBins: (rackId) => api.post(`/racks/${rackId}/generate-bins`),
  listBinsForRack: (rackId, params = {}) => api.get(`/racks/${rackId}/bins`, { params }),

  // Bins
  getBin: (id) => api.get(`/bins/${id}`),
  updateBin: (id, body) => api.patch(`/bins/${id}`, body),
  setBinStatus: (id, status) => api.post(`/bins/${id}/status`, { status }),

  // Warehouse-scoped bin search. Used by the Putaway Workbench's
  // "change bin" picker. ``tenant_id`` / ``booking_id`` restrict the
  // result to the tenant's allocated zones — dropping them returns
  // every bin in the warehouse so hosts can override freely.
  listBins: (params = {}) => api.get('/bins', { params }),
  // Ranked suggestion — mirrors the operations-service response shape
  // so the picker and the workbench can share rendering code.
  listBinsRanked: (params = {}) => api.get('/bins/ranked', { params }),
  // Bulk resolve bin_ids → rack code + zone name. Used by the
  // BookingDetail "Storage Locations" panel which has a list of
  // bin_ids from inventory rows and needs the human-friendly
  // breadcrumbs. Input: array of UUIDs; server de-dupes, skips
  // unknown ids, and returns in caller order.
  resolveBinLocations: (binIds) => {
    const list = Array.isArray(binIds) ? binIds : [binIds];
    const clean = list.filter(Boolean);
    if (clean.length === 0) return Promise.resolve({ data: [] });
    return api.get('/bins/locations', { params: { bin_ids: clean.join(',') } });
  },
};

// ── Adapters ──────────────────────────────────────────────────────────

export const adaptRackFromApi = (r) => {
  if (!r) return null;
  return {
    id: r.id,
    warehouseId: r.warehouse_id,
    zoneId: r.zone_id,
    code: r.code,
    name: r.name || '',
    rackType: r.rack_type || 'SELECTIVE',
    rows: r.rows,
    cols: r.cols,
    shelves: r.shelves,
    binsPerShelf: r.bins_per_shelf,
    capacityKg: r.capacity_kg,
    defaultBinCapacityUnits: r.default_bin_capacity_units,
    isActive: !!r.is_active,
    // Phase 10-1 — floor-plan grid. null means "auto-flow"; an
    // explicit number pins the rack to that cell.
    gridRow: r.grid_row == null ? null : Number(r.grid_row),
    gridCol: r.grid_col == null ? null : Number(r.grid_col),
    binCount: r.bin_count ?? 0,
    occupiedBins: r.occupied_bins ?? 0,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
};

const _numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const adaptRackToApi = (form) => ({
  warehouse_id: form.warehouseId,
  zone_id: form.zoneId,
  code: form.code,
  name: form.name || null,
  rack_type: form.rackType || 'SELECTIVE',
  rows: Number(form.rows) || 1,
  cols: Number(form.cols) || 1,
  shelves: Number(form.shelves) || 1,
  bins_per_shelf: Number(form.binsPerShelf) || 1,
  capacity_kg: _numOrNull(form.capacityKg),
  default_bin_capacity_units: _numOrNull(form.defaultBinCapacityUnits),
  is_active: form.isActive !== false,
  grid_row: _numOrNull(form.gridRow),
  grid_col: _numOrNull(form.gridCol),
});

export const adaptBinFromApi = (b) => {
  if (!b) return null;
  return {
    id: b.id,
    rackId: b.rack_id,
    warehouseId: b.warehouse_id,
    zoneId: b.zone_id,
    code: b.code,
    row: b.row_num,
    col: b.col_num,
    shelf: b.shelf_num,
    bin: b.bin_num,
    status: b.status,
    capacityKg: b.capacity_kg,
    capacityVolumeM3: b.capacity_volume_m3,
    capacityUnits: b.capacity_units,
  };
};

export default spaceService;
